import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../lib/api";
import { track, getAnalyticsContext } from "../lib/analytics";
import { useAuth } from "../contexts/AuthContext";
import { loadQuoteDraft, saveQuoteDraftFile, saveQuoteDraftMeta, deleteQuoteDraft } from "../lib/draftStore";
import { useActiveJob, getActiveJob, setActiveJob, clearActiveJob } from "../lib/analysisJobs";
import AppLayout from "../components/app/AppLayout";
import { ArrowLeft, ArrowRight, Sparkles, Cog, AlertTriangle, Loader2, History, RotateCcw } from "lucide-react";
import StepIndicator from "../components/quote/StepIndicator";
import AnalysisProgress from "../components/quote/AnalysisProgress";
import { parseLot, parseMargin, quoteParamsError } from "../lib/quoteParams";
import CadUploadZone from "../components/quote/CadUploadZone";
import Step3DViewer from "../components/quote/Step3DViewer";
import DrawingReconciliation from "../components/quote/DrawingReconciliation";
import ExtractionConfirmPanel from "../components/quote/ExtractionConfirmPanel";
import Step2MaterialPanel from "../components/quote/Step2MaterialPanel";
import Step3MachinesPanel from "../components/quote/Step3MachinesPanel";
import MissingMachineNotice from "../components/quote/MissingMachineNotice";

// Confirmed process → machine capability needed to perform it in-house.
// Only the sheet-metal pair matters here: a workshop full of lathes has no
// machine able to cut or bend sheet, and the wizard must say so instead of
// silently dead-ending.
const PROCESS_CAPABILITY = {
  sheet_cutting: "sheet_cutting_2d",
  bending: "bending",
};

// Diff the operator's confirmed feature sheet against the raw AI extraction.
// Feeds the QA audit trail: a field edited over and over means the extraction
// prompt is systematically wrong on that pattern.
const computeFeatureEdits = (ai, user) => {
  if (!ai || !user) return [];
  const edits = [];
  const push = (field, a, u) => {
    if (JSON.stringify(a) !== JSON.stringify(u)) edits.push({ field, ai_value: a, user_value: u });
  };
  push("raw_material_form", ai.raw_material_form?.value, user.raw_material_form?.value);
  push(
    "processes",
    (ai.processes || []).map((p) => p.type).sort(),
    (user.processes || []).map((p) => p.type).sort()
  );
  push(
    "external_diameters",
    (ai.external_diameters || []).map((d) => d.value_mm),
    (user.external_diameters || []).map((d) => d.value_mm)
  );
  push(
    "internal_diameters",
    (ai.internal_diameters || []).map((d) => d.value_mm),
    (user.internal_diameters || []).map((d) => d.value_mm)
  );
  push(
    "threads",
    (ai.threads || []).map((th) => `${th.designation}x${th.quantity}`),
    (user.threads || []).map((th) => `${th.designation}x${th.quantity}`)
  );
  push("overall_dimensions_mm", ai.overall_dimensions_mm, user.overall_dimensions_mm);
  return edits;
};

const RASTER = ["png", "jpeg", "jpg", "webp"];

// "Ricomincia" remounts the wizard from scratch via the key.
export default function NewQuotePage() {
  const [resetKey, setResetKey] = useState(0);
  return <NewQuoteWizard key={resetKey} onReset={() => setResetKey((k) => k + 1)} />;
}

function NewQuoteWizard({ onReset }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  // The running analysis lives in an app-level store (survives navigation
  // and reloads); this page only renders its progress.
  const job = useActiveJob(user?.id);
  const analyzing = job?.status === "processing";
  const jobError = job?.status === "failed" ? job.error : "";
  const [submitting, setSubmitting] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const machinesInitRef = useRef(false);
  const [step, setStep] = useState(1);
  const [imageBase64, setImageBase64] = useState("");
  const [imagePreview, setImagePreview] = useState("");
  const [materials, setMaterials] = useState([]);
  const [machines, setMachines] = useState([]);
  const [userMachines, setUserMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [materialId, setMaterialId] = useState("");
  const [ownedMachines, setOwnedMachines] = useState([]);
  // Specific user_machine instance IDs selected (one or many per category).
  // The backend uses these to pick real hourly_rate + apply the inferred
  // efficiency multiplier (twin spindle, dual turret, etc.).
  const [selectedUserMachineIds, setSelectedUserMachineIds] = useState([]);
  const [batchSize, setBatchSize] = useState(1);
  const [marginPct, setMarginPct] = useState(25);
  // Optional: sheet thickness in mm — shown only when the selected material is
  // a sheet/lamiera so the backend can force `geometry_type='sheet'` and
  // prefer sheet-metal machines (press brake, shear, fiber laser, calandra).
  const [sheetThicknessMm, setSheetThicknessMm] = useState("");
  const [currency, setCurrency] = useState("EUR");
  const [customerId, setCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [cadFormat, setCadFormat] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  // Drawings Reconciliation state (extracted callouts + B-Rep features).
  const [drawingReqs, setDrawingReqs] = useState(null);
  const [geomFeatures, setGeomFeatures] = useState(null);
  const [reconLoading, setReconLoading] = useState(false);
  // Single Source of Truth: the AI extraction (immutable reference), the
  // operator's working copy and the blocking confirmation flag.
  const [aiExtraction, setAiExtraction] = useState(null);
  const [featureExtraction, setFeatureExtraction] = useState(null);
  const [extractionConfirmed, setExtractionConfirmed] = useState(false);
  const [extractionId, setExtractionId] = useState(null);
  const [extractionCached, setExtractionCached] = useState(false);
  // Decoded STEP/IGES bytes — used by Step3DViewer in Step 1 to render
  // the part directly without a server roundtrip (quote not yet created).
  const [step3dBytes, setStep3dBytes] = useState(null);
  const fileRef = useRef(null);

  const STEPS = [
    { id: 1, label: t("newQuote.stepLabel1") },
    { id: 2, label: t("newQuote.stepLabelExtraction") },
    { id: 3, label: t("newQuote.stepLabel2") },
    { id: 4, label: t("newQuote.stepLabel3") },
    { id: 5, label: t("newQuote.stepLabel4") },
  ];

  useEffect(() => {
    Promise.all([
      api.get("/catalog/materials"),
      api.get("/catalog/machines"),
      api.get("/customers"),
      api.get("/user-machines"),
    ])
      .then(([m, mc, c, um]) => {
        setMaterials(m.data);
        setMachines(mc.data);
        setCustomers(c.data);
        setUserMachines(um.data || []);
        // Auto-preselect ALL the user's specific machines — they are the real
        // workshop fleet, so making them the default reflects "le mie macchine
        // come prima scelta" from the spec. A restored draft keeps its choice.
        if (!machinesInitRef.current) {
          machinesInitRef.current = true;
          setSelectedUserMachineIds((um.data || []).map((x) => x.id));
          const ownedCats = Array.from(new Set((um.data || []).map((x) => x.category_id)));
          if (ownedCats.length > 0) setOwnedMachines(ownedCats);
        }
      })
      .catch(() => setError(t("newQuote.errorCatalog")));
  }, [t]);

  // Partition catalog machine categories into "owned by user" vs "other".
  // Counting how many of the user's machines fall into each category gives the
  // step-3 UI useful info ("3 macchine in officina") and primes the warning
  // banner that fires when a non-owned category is picked.
  const ownedCategoryIds = new Set(userMachines.map((u) => u.category_id));
  const ownedCategoryMachines = machines.filter((m) => ownedCategoryIds.has(m.id));
  // Generic categories only — brand-specific models (Citizen, FLM Tech, Makino, etc.)
  // live in the "Catalogo macchine" page where the workshop owner can add them
  // to their own park. In the quote wizard we only expose the generic taxonomy.
  const otherCategoryMachines = machines.filter(
    (m) => !ownedCategoryIds.has(m.id) && !m.brand
  );
  const userMachinesByCategory = userMachines.reduce((acc, um) => {
    (acc[um.category_id] = acc[um.category_id] || []).push(um);
    return acc;
  }, {});

  // Auto-apply the matched material from Drawings Reconciliation as the
  // initial selection. The user can still freely pick a different one
  // afterwards (which then surfaces the conflict warning correctly).
  useEffect(() => {
    if (!drawingReqs?.material_callout || !materials.length) return;
    if (materialId) return; // do not override an explicit user choice
    // Replicate the matchMaterial heuristic kept in DrawingReconciliation:
    const callout = drawingReqs.material_callout;
    const fam = callout.normalized_family;
    const grade = (callout.grade_hint || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    let match = null;
    if (grade) {
      match = materials.find((m) =>
        (m.name || "").toLowerCase().replace(/[^a-z0-9]/g, "").includes(grade)
      );
    }
    if (!match && fam) {
      const kws = {
        stainless_steel: ["inox", "stainless"],
        aluminum: ["allumin", "aluminum"],
        carbon_steel: ["carbonio", "carbon", "c40", "c45"],
        brass: ["ottone", "brass"],
        bronze: ["bronzo", "bronze"],
        titanium: ["titanio", "titanium"],
        copper: ["rame", "copper"],
        plastic: ["plast", "peek", "delrin", "pa6", "nylon", "abs", "policarbonato"],
        tool_steel: ["utensili", "tool steel"],
        inconel: ["inconel"],
      }[fam] || [];
      match = materials.find((m) => {
        const hay = `${m.name} ${m.category}`.toLowerCase();
        return kws.some((k) => hay.includes(k));
      });
    }
    if (match) setMaterialId(match.id);
  }, [drawingReqs, materials, materialId]);
  // Detect upload kind from file (covers extensions not in MIME map, e.g. .dxf).
  const detectCadFormat = (file) => {
    const name = (file?.name || "").toLowerCase();
    if (file?.type === "application/pdf" || name.endsWith(".pdf")) return "pdf";
    if (name.endsWith(".dxf")) return "dxf";
    if (name.endsWith(".dwg")) return "dwg";
    if (name.endsWith(".step") || name.endsWith(".stp")) return "step";
    if (name.endsWith(".iges") || name.endsWith(".igs")) return "iges";
    if (name.endsWith(".x_t") || name.endsWith(".x_b")) return "parasolid";
    if (file?.type?.startsWith("image/")) return file.type.replace("image/", "");
    return null;
  };

  // STEP/IGES: decode bytes for the in-browser 3D viewer (no roundtrip).
  const decode3d = (b64) => {
    try {
      const bin = atob(b64);
      const u8 = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      setStep3dBytes(u8);
    } catch (decodeErr) {
      console.warn("STEP byte decode failed:", decodeErr);
    }
  };

  // DXF / PDF / STEP / IGES: ask the backend to render a PNG preview so
  // the user can verify the file decoded correctly before progressing.
  const renderPreview = async (b64, fmt) => {
    setImagePreview(null);
    setPreviewing(true);
    try {
      const { data } = await api.post("/quotes/preview-cad", { image_base64: b64, cad_format: fmt });
      if (data?.png_base64) setImagePreview(`data:image/png;base64,${data.png_base64}`);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("newQuote.errorPreview", { defaultValue: "Anteprima non disponibile, ma il file è stato caricato." }));
    } finally {
      setPreviewing(false);
    }
  };

  // Drawings Reconciliation — the Next button stays disabled until the AI
  // has finished reading/reconciling the drawing (see canNext step 1).
  // If extraction fails the wizard unlocks anyway (reconLoading → false).
  const runExtraction = async (b64, fmt) => {
    setReconLoading(true);
    try {
      const { data } = await api.post("/quotes/extract-drawing-requirements", { image_base64: b64, cad_format: fmt });
      setDrawingReqs(data?.requirements || null);
      if (data?.geometry_features) setGeomFeatures(data.geometry_features);
      if (data?.feature_extraction) {
        setAiExtraction(data.feature_extraction);
        setFeatureExtraction(JSON.parse(JSON.stringify(data.feature_extraction)));
      }
      setExtractionId(data?.extraction_id || null);
      setExtractionCached(!!data?.cached);
    } catch (err) {
      // Non-blocking: surface a small warning but keep the upload.
      console.warn("Drawing reconciliation failed:", err);
    } finally {
      setReconLoading(false);
    }
  };

  const handleFile = (file) => {
    if (!file) return;
    const fmt = detectCadFormat(file);
    if (fmt === "dwg") {
      setError(t("newQuote.errorDwg", { defaultValue: "Formato DWG non supportato. Esportalo come DXF o PDF dal tuo CAD (AutoCAD, Fusion 360, SolidWorks) e ricarica." }));
      return;
    }
    const ok = ["png", "jpeg", "jpg", "webp", "pdf", "dxf", "step", "iges", "parasolid"].includes(fmt);
    if (!ok) {
      setError(t("newQuote.errorFormat"));
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      setError(t("newQuote.errorSize"));
      return;
    }
    setError("");
    setCadFormat(fmt);
    // Reset previous reconciliation when a new file is uploaded.
    setDrawingReqs(null);
    setGeomFeatures(null);
    setStep3dBytes(null);
    setAiExtraction(null);
    setFeatureExtraction(null);
    setExtractionConfirmed(false);
    setExtractionId(null);
    const reader = new FileReader();
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      const b64 = dataUrl.split(",")[1];
      setImageBase64(b64);
      if (fmt === "step" || fmt === "iges") decode3d(b64);
      // Raster images: preview directly in browser, no server roundtrip needed.
      if (RASTER.includes(fmt)) setImagePreview(dataUrl);
      else await renderPreview(b64, fmt);
      await runExtraction(b64, fmt);
    };
    reader.readAsDataURL(file);
  };

  // Draft restore: after a reload, a closed tab or a trip to another page
  // the wizard resumes with the same file, choices and step.
  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    loadQuoteDraft(user.id).then((d) => {
      if (!alive) return;
      if (d) {
        setImageBase64(d.imageBase64);
        setCadFormat(d.cadFormat || null);
        setImagePreview(d.imagePreview || "");
        setDrawingReqs(d.drawingReqs || null);
        setGeomFeatures(d.geomFeatures || null);
        setAiExtraction(d.aiExtraction || null);
        setFeatureExtraction(d.featureExtraction || null);
        setExtractionConfirmed(!!d.extractionConfirmed);
        setExtractionId(d.extractionId || null);
        setExtractionCached(!!d.extractionCached);
        setMaterialId(d.materialId || "");
        setBatchSize(d.batchSize ?? 1);
        setMarginPct(d.marginPct ?? 25);
        setSheetThicknessMm(d.sheetThicknessMm || "");
        setCurrency(d.currency || "EUR");
        setCustomerId(d.customerId || "");
        setNotes(d.notes || "");
        // Machine choices are only meaningful once the user has seen step 4.
        if ((d.step || 1) >= 4 && Array.isArray(d.ownedMachines)) {
          machinesInitRef.current = true;
          setOwnedMachines(d.ownedMachines);
          setSelectedUserMachineIds(d.selectedUserMachineIds || []);
        }
        setStep(d.step || 1);
        setDraftRestored(true);
        if (d.cadFormat === "step" || d.cadFormat === "iges") decode3d(d.imageBase64);
        if (!d.imagePreview && !RASTER.includes(d.cadFormat)) renderPreview(d.imageBase64, d.cadFormat);
        if (!d.featureExtraction) runExtraction(d.imageBase64, d.cadFormat);
        // The tab was closed mid-analysis and the local job record is gone:
        // re-attach to the server job instead of losing the result.
        if (d.jobId && getActiveJob()?.jobId !== d.jobId) {
          setActiveJob({ jobId: d.jobId, userId: user.id, startedAt: d.jobStartedAt || Date.now(), stage: "queued", status: "processing", error: null });
        }
        track("quote_draft_restored", { step: d.step || 1, cad_format: d.cadFormat || null });
      }
      setDraftReady(true);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Draft save (debounced). The heavy file is written only when it changes.
  useEffect(() => {
    if (!draftReady || !user?.id || !imageBase64) return;
    saveQuoteDraftFile(user.id, { imageBase64, cadFormat, imagePreview });
  }, [draftReady, user?.id, imageBase64, cadFormat, imagePreview]);

  useEffect(() => {
    if (!draftReady || !user?.id || !imageBase64) return undefined;
    const id = setTimeout(() => saveQuoteDraftMeta(user.id, {
      drawingReqs, geomFeatures, aiExtraction, featureExtraction, extractionConfirmed, extractionId,
      extractionCached, materialId, ownedMachines, selectedUserMachineIds, batchSize, marginPct,
      sheetThicknessMm, currency, customerId, notes, step,
      jobId: analyzing ? job.jobId : null, jobStartedAt: analyzing ? job.startedAt : null,
    }), 400);
    return () => clearTimeout(id);
  }, [draftReady, user?.id, imageBase64, drawingReqs, geomFeatures, aiExtraction, featureExtraction,
    extractionConfirmed, extractionId, extractionCached, materialId, ownedMachines, selectedUserMachineIds,
    batchSize, marginPct, sheetThicknessMm, currency, customerId, notes, step, analyzing, job?.jobId, job?.startedAt]);

  // Coming back to the wizard while the analysis runs → straight to its progress.
  useEffect(() => { if (analyzing) setStep(5); }, [analyzing]);

  const discardDraft = async () => {
    await deleteQuoteDraft(user.id);
    if (job && !analyzing) clearActiveJob();
    track("quote_draft_discarded", { step });
    onReset();
  };

  const onDrop = (e) => { e.preventDefault(); handleFile(e.dataTransfer.files[0]); };
  const toggleMachine = (id) => setOwnedMachines((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  // Toggle a specific user_machine instance. We also keep its category in
  // ownedMachines so the backend filter on capability still works.
  const toggleUserMachine = (umId, categoryId) => {
    setSelectedUserMachineIds((prev) => {
      const has = prev.includes(umId);
      const next = has ? prev.filter((x) => x !== umId) : [...prev, umId];
      // If deselecting the last user_machine of a category, also drop the
      // category from ownedMachines (unless the user manually checked the
      // catalog row).
      if (has) {
        const stillOwnedInCat = next.some((id) => {
          const um = userMachines.find((u) => u.id === id);
          return um && um.category_id === categoryId;
        });
        if (!stillOwnedInCat) {
          setOwnedMachines((m) => m.filter((c) => c !== categoryId));
        }
      } else if (!ownedMachines.includes(categoryId)) {
        setOwnedMachines((m) => [...m, categoryId]);
      }
      return next;
    });
  };

  // Client-side mirror of backend `infer_machine_efficiency` — kept tiny so
  // the UI can preview the productivity bonus next to each card.
  // Keep keyword lists in sync with /app/backend/machine_efficiency.py
  const inferEfficiency = (um) => {
    const text = `${um.name || ""} ${um.model || ""} ${um.notes || ""}`.toLowerCase();
    const reasons = [];
    let mult = 1.0;
    if (um.category_id === "tornio_cnc") {
      if (/twin spindle|doppio mandrino|contromandrino|sub.?spindle/.test(text)) { mult *= 0.65; reasons.push(t("newQuote.effSubSpindle", { defaultValue: "Contromandrino" })); }
      if (/multitorretta|doppia torretta|twin.?turret|dual.?turret|double.?turret/.test(text)) { mult *= 0.75; reasons.push(t("newQuote.effTwinTurret", { defaultValue: "Doppia torretta" })); }
      if (/asse y|y.?axis|utensili motorizzati|live tools|driven tools|motorizzati/.test(text)) { mult *= 0.90; reasons.push(t("newQuote.effLiveTools", { defaultValue: "Utensili motorizzati / asse Y" })); }
      if (reasons.length === 0) {
        const axes = parseInt(um.axes || 0, 10);
        if (axes >= 8) { mult *= 0.55; reasons.push(t("newQuote.effMultiAxis8", { defaultValue: "8+ assi multi-tasking" })); }
        else if (axes >= 6) { mult *= 0.70; reasons.push(t("newQuote.effMultiAxis6", { defaultValue: "6 assi" })); }
        else if (axes >= 4) { mult *= 0.90; reasons.push(t("newQuote.effMultiAxis4", { defaultValue: "4 assi turn-mill" })); }
      }
    } else if (["centro_lavoro", "fresatrice_cnc_5", "fresatrice_cnc_3"].includes(um.category_id)) {
      if (/pallet|apc/.test(text)) { mult *= 0.92; reasons.push(t("newQuote.effPallet", { defaultValue: "Cambio pallet automatico" })); }
      if (/monoblock|mono.?block/.test(text)) { mult *= 0.97; reasons.push(t("newQuote.effMonoblock", { defaultValue: "Struttura monoBLOCK" })); }
      if (/motori lineari|linear motor/.test(text)) { mult *= 0.92; reasons.push(t("newQuote.effLinearMotor", { defaultValue: "Motori lineari" })); }
      if (reasons.length === 0 && parseInt(um.axes || 0, 10) >= 5) { mult *= 0.85; reasons.push(t("newQuote.eff5Axis", { defaultValue: "5 assi simultanei" })); }
    }
    return { mult: Math.max(Math.round(mult * 1000) / 1000, 0.35), reasons };
  };

  const canNext = () => {
    if (step === 1) return !!imageBase64 && !previewing && !reconLoading;
    // BLOCKING GATE: the operator must verify the extracted features before
    // the quote can progress. No confirmation → no next step.
    if (step === 2) return !!featureExtraction && extractionConfirmed;
    if (step === 3) return !!materialId;
    if (step === 4) return ownedMachines.length > 0 && !quoteParamsError(batchSize, marginPct, t);
    return false;
  };

  // Processes the operator confirmed that none of the selected machines can
  // perform (sheet cutting / bending on a workshop with only lathes & mills).
  const uncoveredProcesses = (featureExtraction?.processes || [])
    .map((p) => p.type)
    .filter((type) => {
      const cap = PROCESS_CAPABILITY[type];
      if (!cap) return false;
      return !ownedMachines.some((catId) =>
        (machines.find((m) => m.id === catId)?.capabilities || []).includes(cap),
      );
    });

  // Funnel instrumentation: which wizard step the user saw, and whether they
  // left the wizard without launching the analysis.
  const stepLabel = (n) => STEPS.find((s) => s.id === n)?.label || String(n);
  useEffect(() => {
    track("quote_step_viewed", { step, step_name: stepLabel(step), has_file: !!imageBase64 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const progressRef = useRef({ step: 1, submitted: false });
  progressRef.current.step = step;
  if (analyzing) progressRef.current.submitted = true;
  useEffect(() => () => {
    const { step: lastStep, submitted } = progressRef.current;
    if (!submitted) {
      track("quote_abandoned", { step: lastStep, step_name: stepLabel(lastStep) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Async job pattern: POST starts a background job on the server; the
  // app-level AnalysisJobWatcher polls it, so the analysis keeps running and
  // gets reported even if the user leaves this page or closes the tab.
  // `quote_generated` is emitted by the backend when the quote is saved.
  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    progressRef.current.submitted = true;
    setError("");
    if (job) clearActiveJob();
    try {
      const { data: started } = await api.post("/quotes/analyze-async", {
        image_base64: imageBase64, material_id: materialId, owned_machine_ids: ownedMachines,
        user_machine_ids: selectedUserMachineIds,
        batch_size: parseLot(batchSize), margin_pct: parseMargin(marginPct),
        currency, customer_id: customerId || null, notes,
        cad_format: cadFormat || undefined,
        drawing_requirements: drawingReqs || undefined,
        sheet_thickness_mm: sheetThicknessMm ? parseFloat(sheetThicknessMm) : undefined,
        feature_extraction: featureExtraction || undefined,
        features_confirmed: extractionConfirmed,
        feature_edits: computeFeatureEdits(aiExtraction, featureExtraction),
        extraction_id: extractionId || undefined,
        ...getAnalyticsContext(),
      }, { timeout: 55000 });
      setActiveJob({ jobId: started.job_id, userId: user.id, startedAt: Date.now(), stage: "queued", status: "processing", error: null });
    } catch (e) {
      track("quote_generation_failed", { reason: e.response?.status || "network", elapsed_ms: 0 });
      setError(formatApiError(e.response?.data?.detail) || t("newQuote.errorAnalysis"));
    }
    setSubmitting(false);
  };

  const shownError = error || jobError;

  return (
    <AppLayout>
      <div className="px-4 sm:px-8 py-8 max-w-6xl mx-auto" data-testid="new-quote-page">
        <button onClick={() => (window.history.state?.idx > 0 ? navigate(-1) : navigate("/dashboard"))} className="text-slate-400 hover:text-slate-200 text-sm flex items-center gap-2 mb-4" data-testid="back-btn">
          <ArrowLeft size={14}/> {t("common.back")}
        </button>
        <div className="label-tech mb-2">{t("newQuote.wizardLabel")}</div>
        <h1 className="font-display font-bold text-3xl text-slate-100 mb-6">{t("newQuote.title")}</h1>

        <StepIndicator steps={STEPS} currentStep={step} />

        {draftRestored && (
          <div className="mb-4 px-4 py-3 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm flex items-center justify-between gap-3 flex-wrap" data-testid="draft-restored-banner">
            <span className="flex items-center gap-2"><History size={16}/> {t("draft.restored")}</span>
            {!analyzing && (
              <button type="button" onClick={discardDraft} className="text-xs px-3 py-1.5 rounded-md border border-emerald-500/40 hover:bg-emerald-500/10 flex items-center gap-1.5" data-testid="draft-discard-btn">
                <RotateCcw size={12}/> {t("draft.discard")}
              </button>
            )}
          </div>
        )}

        {shownError && (
          <div className="mb-4 px-4 py-3 rounded-md bg-red-500/10 border border-red-500/40 text-red-300 text-sm flex items-center gap-2" data-testid="quote-error">
            <AlertTriangle size={16}/> {shownError}
          </div>
        )}

        <div className="tech-card p-4 sm:p-8 fade-in-up">
          {step === 1 && (
            <div data-testid="step-upload">
              <div className="label-tech mb-2">{t("common.step")} 1</div>
              <h2 className="font-display font-bold text-xl text-slate-100 mb-1">{t("newQuote.step1Title")}</h2>
              <p className="text-slate-400 text-sm mb-6">{t("newQuote.step1Body")}</p>

              {/* Two-column layout once a file is loaded:
                  left = big Fictiv-style preview (3D viewer for STEP, image otherwise)
                  right = Drawings Reconciliation panel with declared callouts. */}
              {imageBase64 ? (
                <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-5">
                  <div data-testid="upload-preview-area">
                    {step3dBytes ? (
                      <Step3DViewer
                        rawBytes={step3dBytes}
                        holes={geomFeatures?.candidate_holes || []}
                        partBBox={geomFeatures?.bbox_mm || null}
                        height={520}
                        theme="light"
                      />
                    ) : (
                      <div className="rounded-xl border border-slate-300 bg-white shadow-sm overflow-hidden" data-testid="upload-2d-preview">
                        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between text-xs font-mono uppercase tracking-wider text-slate-700">
                          <span>{t("newQuote.drawingPreview")}{cadFormat ? ` · ${cadFormat.toUpperCase()}` : ""}</span>
                          <button
                            type="button"
                            onClick={() => fileRef.current?.click()}
                            disabled={reconLoading || previewing}
                            className="text-[11px] px-2 py-0.5 rounded border border-slate-300 text-slate-600 hover:border-slate-400 normal-case disabled:opacity-40 disabled:cursor-not-allowed"
                            data-testid="replace-file-btn"
                          >
                            {t("newQuote.replaceFile")}
                          </button>
                        </div>
                        <div className="bg-gradient-to-br from-slate-50 via-white to-slate-100 min-h-[420px] max-h-[520px] flex items-center justify-center overflow-auto p-6">
                          {previewing ? (
                            <div className="flex flex-col items-center gap-3 text-slate-500 text-sm">
                              <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"/>
                              <span>Rendering anteprima…</span>
                            </div>
                          ) : imagePreview ? (
                            <img
                              src={imagePreview}
                              alt="part preview"
                              className="max-w-full max-h-[480px] object-contain"
                              data-testid="upload-preview-img"
                            />
                          ) : (
                            <div className="text-slate-500 text-sm">{t("common.previewUnavailable")}</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    <DrawingReconciliation
                      requirements={drawingReqs}
                      features={geomFeatures}
                      materials={materials}
                      selectedMaterialId={materialId}
                      onApplyMaterial={setMaterialId}
                      loading={reconLoading}
                    />
                  </div>
                </div>
              ) : (
                <CadUploadZone
                  t={t}
                  previewing={previewing}
                  imagePreview={imagePreview}
                  imageBase64={imageBase64}
                  cadFormat={cadFormat}
                  onDrop={onDrop}
                  fileRef={fileRef}
                />
              )}
              <input ref={fileRef} type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.dxf,.step,.stp,.iges,.igs,.x_t,.x_b,image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => handleFile(e.target.files[0])} className="hidden" data-testid="file-input"/>
            </div>
          )}

          {step === 2 && (
            <ExtractionConfirmPanel
              extraction={featureExtraction}
              onChange={setFeatureExtraction}
              confirmed={extractionConfirmed}
              onConfirm={() => setExtractionConfirmed(true)}
              loading={reconLoading}
              cached={extractionCached}
            />
          )}

          {step === 3 && (
            <Step2MaterialPanel
              t={t}
              drawingReqs={drawingReqs}
              geomFeatures={geomFeatures}
              reconLoading={reconLoading}
              materials={materials}
              materialId={materialId}
              setMaterialId={setMaterialId}
              sheetThicknessMm={sheetThicknessMm}
              setSheetThicknessMm={setSheetThicknessMm}
            />
          )}

          {step === 4 && (
            <>
              {ownedMachines.length === 0 && (
                <MissingMachineNotice
                  variant="none"
                  onPickFromCatalog={() => document.querySelector('[data-testid="catalog-machines-grid"]')?.scrollIntoView({ behavior: "smooth", block: "start" })}
                />
              )}
              {ownedMachines.length > 0 && uncoveredProcesses.length > 0 && (
                <MissingMachineNotice variant="process" processes={uncoveredProcesses} />
              )}
              <Step3MachinesPanel
              t={t}
              userMachines={userMachines}
              selectedUserMachineIds={selectedUserMachineIds}
              toggleUserMachine={toggleUserMachine}
              otherCategoryMachines={otherCategoryMachines}
              ownedMachines={ownedMachines}
              toggleMachine={toggleMachine}
              inferEfficiency={inferEfficiency}
              batchSize={batchSize}
              setBatchSize={setBatchSize}
              marginPct={marginPct}
              setMarginPct={setMarginPct}
              currency={currency}
              setCurrency={setCurrency}
              customers={customers}
              customerId={customerId}
              setCustomerId={setCustomerId}
              notes={notes}
              setNotes={setNotes}
            />
            </>
          )}

          {step === 5 && (
            <div data-testid="step-analyze" className="text-center py-8">
              {analyzing ? (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4 pulse-blue">
                    <Sparkles size={28} className="text-blue-400 animate-pulse"/>
                  </div>
                  <h2 className="font-display font-bold text-xl text-slate-100 mb-5">{t("newQuote.analyzing")}</h2>
                  <AnalysisProgress stage={job.stage} startedAt={job.startedAt} />
                </>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-blue-500/10 flex items-center justify-center mb-4">
                    <Cog size={28} className="text-blue-400"/>
                  </div>
                  <h2 className="font-display font-bold text-xl text-slate-100 mb-2">{t("newQuote.readyTitle")}</h2>
                  <p className="text-slate-400 text-sm mb-6">
                    {t("newQuote.readySummary")}: <span className="text-slate-100 font-medium">{(() => { const m = materials.find(x=>x.id===materialId); return m ? t(`materials.${m.id}.name`, { defaultValue: m.name }) : ""; })()}</span> · <span className="text-slate-100">{t("newQuote.machinesCount", { n: ownedMachines.length })}</span> · {t("dashboard.lot").toLowerCase()} {batchSize}
                  </p>
                  {uncoveredProcesses.length > 0 && (
                    <div className="max-w-xl mx-auto text-left">
                      <MissingMachineNotice variant="process" processes={uncoveredProcesses} />
                    </div>
                  )}
                  <button onClick={submit} disabled={submitting || !imageBase64} className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-md disabled:opacity-60" data-testid="analyze-submit">
                    {submitting ? <Loader2 size={16} className="animate-spin"/> : <Sparkles size={16}/>} {t("newQuote.startAnalysis")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-6">
          <button onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1 || analyzing}
            className="px-4 py-2 rounded-md text-sm text-slate-300 border border-slate-700 hover:border-slate-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="prev-step">
            <ArrowLeft size={14}/> {t("common.back")}
          </button>
          {step < 5 && (
            <button onClick={() => setStep((s) => Math.min(5, s + 1))} disabled={!canNext()} className="btn-primary px-5 py-2.5 rounded-md flex items-center gap-2" data-testid="next-step">
              {step === 1 && reconLoading ? (
                <><Loader2 size={14} className="animate-spin"/> {t("newQuote.reconWait")}</>
              ) : (
                <>{t("common.next")} <ArrowRight size={14}/></>
              )}
            </button>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
